/**
 * Notification Onboarding Modal
 *
 * Two-step modal that appears after first task creation:
 * Step 1: Pitch - "Never miss a tank task"
 * Step 2: Preferences - Time and threshold selection
 */
import { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  Radio,
  Title,
  ThemeIcon,
  Center,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBell, IconCheck } from '@tabler/icons-react';
import {
  useUpdateNotificationPreferences,
  useIncrementPromptCount,
} from '@/hooks/useNotificationPreferences';
import type { DigestThreshold } from '@/types/notifications';
import { DIGEST_THRESHOLD_LABELS, TIME_PRESETS } from '@/types/notifications';

interface NotificationOnboardingModalProps {
  opened: boolean;
  onClose: () => void;
}

type Step = 'pitch' | 'preferences';

export function NotificationOnboardingModal({
  opened,
  onClose,
}: NotificationOnboardingModalProps) {
  const [step, setStep] = useState<Step>('pitch');
  const [selectedTime, setSelectedTime] = useState('08:00:00');
  const [selectedThreshold, setSelectedThreshold] = useState<DigestThreshold>('due_today');

  const { mutateAsync: updatePreferences, isPending: isSaving } =
    useUpdateNotificationPreferences();
  const { mutateAsync: incrementPrompt } = useIncrementPromptCount();

  const handleNotNow = async () => {
    try {
      await incrementPrompt();
    } catch {
      // Silent fail - not critical
    }
    handleClose();
  };

  const handleSetItUp = () => {
    setStep('preferences');
  };

  const handleDone = async () => {
    try {
      await updatePreferences({
        enabled: true,
        preferred_time: selectedTime,
        digest_threshold: selectedThreshold,
        setup_completed: true,
      });

      notifications.show({
        title: 'Notifications enabled',
        message: "You'll receive daily task reminders.",
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      handleClose();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to save preferences. Please try again.',
        color: 'red',
      });
    }
  };

  const handleClose = () => {
    setStep('pitch');
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={null}
      size="sm"
      centered
      withCloseButton={false}
    >
      {step === 'pitch' ? (
        <Stack gap="lg" py="md">
          <Center>
            <ThemeIcon size={60} radius="xl" color="blue" variant="light">
              <IconBell size={32} />
            </ThemeIcon>
          </Center>

          <Stack gap="xs" ta="center">
            <Title order={3}>Never miss a tank task</Title>
            <Text c="dimmed">
              Get a single daily digest when tasks need your attention.
              No spam - only when something&apos;s due.
            </Text>
          </Stack>

          <Group justify="center" gap="md" mt="md">
            <Button variant="default" onClick={handleNotNow}>
              Not now
            </Button>
            <Button onClick={handleSetItUp}>
              Set it up
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="lg" py="md">
          <Stack gap="xs">
            <Text fw={500}>When should we check in?</Text>
            <Group gap="xs">
              {TIME_PRESETS.map((preset) => (
                <Button
                  key={preset.value}
                  variant={selectedTime === preset.value ? 'filled' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedTime(preset.value)}
                >
                  {preset.label}
                </Button>
              ))}
            </Group>
          </Stack>

          <Stack gap="xs">
            <Text fw={500}>What&apos;s worth a notification?</Text>
            <Radio.Group
              value={selectedThreshold}
              onChange={(v) => setSelectedThreshold(v as DigestThreshold)}
            >
              <Stack gap="xs">
                <Radio
                  value="overdue_only"
                  label={DIGEST_THRESHOLD_LABELS.overdue_only}
                />
                <Radio
                  value="due_today"
                  label={DIGEST_THRESHOLD_LABELS.due_today}
                />
                <Radio
                  value="include_upcoming"
                  label={DIGEST_THRESHOLD_LABELS.include_upcoming}
                />
              </Stack>
            </Radio.Group>
          </Stack>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={handleNotNow}>
              Not now
            </Button>
            <Button onClick={handleDone} loading={isSaving}>
              Done
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
